/**
 * Safely extract a human-readable error message from an Axios error.
 * Handles FastAPI/Pydantic v2 validation errors where detail is an array of
 * objects with { type, loc, msg, input, ctx } keys.
 */

/** The server's `detail` strings a coordinator can actually reach, in Hebrew.
 *
 *  Every HTTPException in the England backend raises English, and getApiError
 *  prefers the server's detail over the page's fallback - so the page can be
 *  written entirely in Hebrew and still show an English sentence the moment
 *  anything goes wrong. That is the moment she is least able to absorb it.
 *
 *  Matched on a substring rather than equality: the details carry ids and names
 *  ("Lead 412 not found"), and matching the whole string would quietly stop
 *  working the first time somebody adds one.
 */
const SERVER_ERRORS: { match: string; hebrew: string }[] = [
  { match: 'Incorrect email or password', hebrew: 'דוא"ל או סיסמה שגויים' },
  { match: 'do not have access to the HMO app', hebrew: 'למשתמש זה אין הרשאה לפורטל הקופות' },
  { match: 'do not have access to the clinic app', hebrew: 'למשתמש זה אין הרשאה למערכת זו' },
  { match: 'Inactive user', hebrew: 'המשתמש אינו פעיל' },
  { match: 'Invalid or expired reset token', hebrew: 'הקישור אינו תקף או שתוקפו פג' },
  { match: 'Invalid 2FA token', hebrew: 'קוד האימות שגוי' },
  { match: 'Could not validate credentials', hebrew: 'תוקף ההתחברות פג. נא להתחבר מחדש.' },
  { match: 'Not authenticated', hebrew: 'תוקף ההתחברות פג. נא להתחבר מחדש.' },
  { match: 'not have permission to view leads', hebrew: 'למשתמש זה אין הרשאה לצפות בהפניות' },
  { match: 'not configured with a sub clinic', hebrew: 'למשתמש זה לא הוגדר מחוז' },
  { match: 'already exists', hebrew: 'הפנייה עבור תעודת הזהות הזו כבר קיימת' },
  { match: 'Lead not found', hebrew: 'ההפנייה לא נמצאה' },
  { match: 'File not found', hebrew: 'הקובץ לא נמצא' },
];

/** English server detail in, Hebrew out - or the original when unrecognised.
 *
 *  Unrecognised text is returned rather than swallowed into a generic message:
 *  a detail nobody translated yet is still more use to her, and to whoever she
 *  calls, than "something went wrong".
 */
export function translateServerError(raw: string): string {
  const known = SERVER_ERRORS.find((e) => raw.includes(e.match));
  return known ? known.hebrew : raw;
}

export function getApiError(err: any, fallback: string): string {
  const detail = err?.response?.data?.detail;
  if (!detail) return fallback;
  if (typeof detail === 'string') return translateServerError(detail);
  if (Array.isArray(detail)) {
    return detail
      .map((d: any) => (typeof d === 'string' ? d : d.msg || JSON.stringify(d)))
      .join('; ');
  }
  if (typeof detail === 'object' && detail.message) return translateServerError(detail.message);
  return fallback;
}
